import React, { useState } from 'react';
import colors from '../../utils/colors';
import typography from '../../utils/typography';
import { UploadIcon, LyricsIcon, RecordIcon } from '../../utils/icons';
import ChatInput from '../ui/ChatInput';
import ChatsView from '../layout/ChatsView';
import SongCellLoading from '../ui/SongCellLoading';
import geminiService from '../../services/geminiService';

const ActionButton = ({ icon: Icon, label, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center gap-3 rounded-2xl p-6 w-full aspect-square hover:opacity-90 transition-opacity"
      style={{ backgroundColor: colors.bg.fog.thin }}
    >
      <div className="w-8 h-8" style={{ color: colors.text.primary }}>
        <Icon />
      </div>
      <span
        className="text-sm"
        style={{
          ...typography.small.title,
          color: colors.text.primary
        }}
      >
        {label}
      </span>
    </button>
  );
};

const Chat = ({
  onModeChange,
  onPlaySong,
  onSelectSong,
  selectedSongTitle,
  selectedSongForForm,
  messages,
  setMessages,
  hasMessages,
  setHasMessages,
  playingSongIndex,
  setPlayingSongIndex,
  selectedSongIndex,
  setSelectedSongIndex,
  onSongSelected,
  onCreateWorkspace,
  onOpenWorkspace,
  isCreating = false,
  onOpenLyricsEditor,
  onOpenStylesEditor,
  onRegisterSendMessage,
  selectedVersion,
  onVersionChange,
  hasPlaybar = false
}) => {
  const [referencedLyrics, setReferencedLyrics] = React.useState(null);
  const [isStyleReference, setIsStyleReference] = React.useState(false);

  // Forward handleSendMessage to parent via callback
  React.useEffect(() => {
    if (onRegisterSendMessage) {
      onRegisterSendMessage(() => handleSendMessage);
    }
  }, [onRegisterSendMessage]);

  const handleSwitchToCustom = () => {
    if (onModeChange) {
      onModeChange('Custom');
    }
  };

  const handleUploadAudio = () => {
    console.log('Upload audio clicked');
  };

  const handleRecordAudio = () => {
    console.log('Record audio clicked');
  };

  const handleWriteLyrics = () => {
    console.log('Write lyrics clicked');
  };

  const handleSelectSong = (songIndex) => {
    console.log('handleSelectSong called with index:', songIndex);

    // Find the song data
    const playersMessage = messages.find(msg => msg.type === 'players');
    const lyricsMessage = messages.find(msg => msg.type === 'lyrics');

    if (playersMessage) {
      const song = playersMessage.songs[songIndex];
      if (song) {
        // Check if we're deselecting (same song) or selecting (different song)
        const isDeselecting = selectedSongTitle === song.title;

        // Call the parent's select handler which manages the shared state
        if (onSelectSong) {
          if (isDeselecting) {
            onSelectSong(null);
          } else {
            onSelectSong({
              title: song.title,
              genres: song.genres,
              image: song.image,
              lyrics: lyricsMessage?.lyrics || '' // Include lyrics for form population
            });
          }
        }

        // Update local selectedSongIndex for form population
        if (isDeselecting) {
          setSelectedSongIndex(null);
          if (onSongSelected) {
            onSongSelected(null);
          }
        } else {
          setSelectedSongIndex(songIndex);
          if (onSongSelected) {
            // Notify parent that a song was selected for form population
            onSongSelected({
              title: song.title,
              genres: song.genres,
              lyrics: lyricsMessage?.lyrics || '',
              image: song.image
            });
          }
        }
      }
    }
  };

  const handlePlaySong = (songIndex) => {
    console.log('handlePlaySong called with index:', songIndex, 'current playing:', playingSongIndex);

    // Find the song data
    const playersMessage = messages.find(msg => msg.type === 'players');

    if (playingSongIndex === songIndex) {
      // Pause if already playing
      console.log('Pausing song');
      setPlayingSongIndex(null);
    } else {
      // Play the song
      console.log('Playing song:', songIndex);
      setPlayingSongIndex(songIndex);

      // Find the song data and pass it to the parent to show in playbar
      if (playersMessage && onPlaySong) {
        const song = playersMessage.songs[songIndex];
        if (song) {
          onPlaySong({
            id: songIndex,
            title: song.title,
            genres: song.genres,
            image: song.image,
            duration: "2:48",
            status: "active"
          });
        }
      }
    }
  };

  const handleSendMessage = async (message) => {
    // Check if this is the first message in an empty chat
    const isFirstMessage = messages.length === 0;

    // Check if this message contains edited lyrics or styles from the canvas
    const isLyricsEdit = message.startsWith('Make a new version of the song with these lyrics:');
    const isStylesEdit = message.startsWith('Make a new version of the song with these styles:');
    let extractedLyrics = null;
    let extractedStyles = null;

    if (isLyricsEdit) {
      // Extract the lyrics part after the header
      extractedLyrics = message.replace('Make a new version of the song with these lyrics:\n\n', '');
    } else if (isStylesEdit) {
      // Extract the styles part after the header
      extractedStyles = message.replace('Make a new version of the song with these styles:\n\n', '');
    }

    // Add outgoing message and loading state
    setMessages(prev => [
      ...prev,
      {
        type: 'outgoing',
        content: message
      },
      {
        type: 'loading'
      }
    ]);
    setHasMessages(true);

    try {
      // Call Gemini API
      const response = await geminiService.sendMessage(message);
      console.log('Gemini response:', response);

      // Parse the JSON response
      const songData = JSON.parse(response);

      // Store workspace ID for later navigation
      let workspaceId = null;

      // If this is the first message and songs were created, create a new workspace
      if (isFirstMessage && songData.songs && songData.songs.length > 0 && onCreateWorkspace) {
        // Extract base title from first song (remove version number)
        const firstSongTitle = songData.songs[0].title;
        const workspaceTitle = firstSongTitle.replace(/\s*\(#\d+\)$/, '');

        // Get the image from the first song (will be set later when rendered)
        const songImage = `https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=60&h=50&fit=crop`;

        // Prepare songs for the workspace
        const workspaceSongs = songData.songs.map((song, index) => ({
          title: song.title,
          genres: Array.isArray(song.genres) ? song.genres.join(', ') : song.genres,
          image: `https://images.unsplash.com/photo-${index === 0 ? '1507525428034-b723cf961d3e' : '1618005182384-a83a8bd57fbe'}?w=160&h=160&fit=crop`,
          songIndex: index
        }));

        console.log('Creating new workspace:', workspaceTitle);
        workspaceId = onCreateWorkspace(workspaceTitle, songImage, workspaceSongs);
      }

      // Remove loading immediately and show typing message
      setMessages(prev => {
        const withoutLoading = prev.filter(msg => msg.type !== 'loading');
        return [
          ...withoutLoading,
          {
            type: 'typing',
            content: songData.reply,
            onComplete: () => {
              // After typing completes, add lyrics with fade-in (if lyrics exist)
              setMessages(currentMessages => {
                const withoutTyping = currentMessages.filter(msg => msg.type !== 'typing' && msg.type !== 'loading');
                const newMessages = [
                  ...withoutTyping,
                  {
                    type: 'incoming',
                    content: songData.reply
                  }
                ];

                // Determine lyrics to display - either from API response or extracted from message
                const lyricsText = songData.lyrics
                  ? songData.lyrics.sections
                      .map(section => `[${section.type}]\n${section.content}`)
                      .join('\n\n')
                  : extractedLyrics;

                if (lyricsText) {
                  // Get styles - either from extracted styles, API response, or first song's genres
                  const styles = extractedStyles || (songData.songs && songData.songs[0]
                    ? (Array.isArray(songData.songs[0].genres)
                        ? songData.songs[0].genres.join(', ')
                        : songData.songs[0].genres)
                    : null);

                  newMessages.push({
                    type: 'lyrics',
                    lyrics: lyricsText,
                    styles: styles,
                    onEdit: (selectedSongTitle) => {
                      // Get as much lyrics as possible (remove section headers and join)
                      const lyricsOnly = lyricsText
                        .split('\n')
                        .filter(line => line.trim() && !line.startsWith('['))
                        .join(' ');

                      // If a song is selected, reference lyrics with the song
                      if (selectedSongTitle) {
                        setReferencedLyrics(`${selectedSongTitle} - ${lyricsOnly}`);
                      } else {
                        setReferencedLyrics(lyricsOnly);
                      }
                      setIsStyleReference(false);
                    },
                    onStylesEdit: (styles) => {
                      // Reference the styles when edit button is clicked
                      setReferencedLyrics(styles); // Only show the genres in the reference header
                      setIsStyleReference(true);
                    },
                    fadeIn: true
                  });
                }

                return newMessages;
              });

              // After lyrics fade in (500ms), add players and suggestions
              setTimeout(() => {
                setMessages(prev => [
                  ...prev,
                  {
                    type: 'players',
                    songs: songData.songs.map((song, index) => ({
                      title: song.title,
                      genres: Array.isArray(song.genres) ? song.genres.join(', ') : song.genres,
                      image: `https://images.unsplash.com/photo-${index === 0 ? '1507525428034-b723cf961d3e' : '1618005182384-a83a8bd57fbe'}?w=160&h=160&fit=crop`,
                      songIndex: index,
                      onThumbsUp: () => console.log('Thumbs up'),
                      onThumbsDown: () => console.log('Thumbs down'),
                      onShare: () => console.log('Share'),
                      onAdd: () => console.log('Add'),
                      onMore: () => console.log('More')
                    })),
                    fadeIn: true
                  },
                  {
                    type: 'createMore',
                    fadeIn: true
                  }
                ]);
              }, 500);
            }
          }
        ];
      });
    } catch (error) {
      console.error('Error sending message:', error);

      // Remove loading and show error message
      setMessages(prev => [
        ...prev.filter(msg => msg.type !== 'loading'),
        {
          type: 'incoming',
          content: 'Sorry, something went wrong. Please try again.'
        }
      ]);
    }
  };

  const handleAttach = () => {
    console.log('Attach clicked');
  };

  const handleRecord = () => {
    console.log('Record clicked');
  };

  const handleCreateMore = () => {
    console.log('Create more clicked');
  };

  return (
    <div className="relative h-full flex flex-col">
      {hasMessages ? (
        /* Chats View */
        <div className="flex-1 overflow-y-auto" style={{ paddingBottom: selectedSongIndex !== null ? '280px' : '160px' }}>
          <ChatsView
            messages={messages.map(msg => {
              if (msg.type === 'players') {
                return {
                  ...msg,
                  songs: msg.songs.map((song) => ({
                    ...song,
                    isPlaying: playingSongIndex === song.songIndex,
                    isSelected: selectedSongTitle === song.title
                  }))
                };
              }
              return msg;
            })}
            onCreateMore={handleCreateMore}
            onPlaySong={handlePlaySong}
            onSelectSong={handleSelectSong}
            onTextSelect={(text) => setReferencedLyrics(text)}
            selectedSongTitle={selectedSongTitle}
            onOpenLyricsEditor={onOpenLyricsEditor}
            onOpenStylesEditor={onOpenStylesEditor}
          />

          {/* Show loading cells while creating */}
          {isCreating && (
            <div className="px-6 pb-6">
              <SongCellLoading />
              <SongCellLoading />
            </div>
          )}
        </div>
      ) : (
        /* Empty State Content - Fills remaining space */
        <div className="flex-1 flex flex-col items-center justify-center p-8 pb-32">
        <div className="flex flex-col items-center gap-2 max-w-md w-full">
          {/* Heading */}
          <h1
            className="text-4xl text-center"
            style={{
              ...typography.large.title,
              fontSize: '32px',
              lineHeight: '40px',
              fontWeight: 500,
              color: colors.text.primary
            }}
          >
            Make any song you can imagine
          </h1>

          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3 w-full">
            <ActionButton
              icon={UploadIcon}
              label="Upload audio"
              onClick={handleUploadAudio}
            />
            <ActionButton
              icon={RecordIcon}
              label="Record audio"
              onClick={handleRecordAudio}
            />
            <ActionButton
              icon={LyricsIcon}
              label="Write lyrics"
              onClick={handleWriteLyrics}
            />
          </div>

          {/* Switch to Custom Button */}
          <button
            onClick={handleSwitchToCustom}
            className="w-full py-[18px] px-32 rounded-2xl hover:opacity-90 transition-opacity"
            style={{
              backgroundColor: colors.bg.fog.thin,
              ...typography.small.regular,
              color: colors.text.primary
            }}
          >
            Open Custom
          </button>
        </div>
      </div>
      )}

      {/* Chat Input - Fixed at absolute bottom */}
      <div className="absolute left-0 right-0" style={{ bottom: hasPlaybar ? '84px' : '0' }}>
        <ChatInput
          onSend={handleSendMessage}
          onAttach={handleAttach}
          onRecord={handleRecord}
          selectedSong={(() => {
            // If there's a selected song title (from either Chat or Workspaces), find it
            if (selectedSongTitle) {
              const playersMessage = messages.find(msg => msg.type === 'players');
              if (playersMessage) {
                const song = playersMessage.songs.find(s => s.title === selectedSongTitle);
                if (song) return song;
              }
              // If not found in messages, use selectedSongForForm (from workspace)
              if (selectedSongForForm && selectedSongForForm.title === selectedSongTitle) {
                return {
                  title: selectedSongForForm.title,
                  genres: selectedSongForForm.genres,
                  image: selectedSongForForm.image
                };
              }
            }
            return null;
          })()}
          onClearSelectedSong={() => {
            setSelectedSongIndex(null);
            if (onSelectSong) {
              onSelectSong(null);
            }
            if (onSongSelected) {
              onSongSelected(null);
            }
          }}
          referencedLyrics={referencedLyrics}
          isStyleReference={isStyleReference}
          onClearReferencedLyrics={() => {
            setReferencedLyrics(null);
            setIsStyleReference(false);
            // Also deselect the song when clearing referenced lyrics
            setSelectedSongIndex(null);
            if (onSelectSong) {
              onSelectSong(null);
            }
            if (onSongSelected) {
              onSongSelected(null);
            }
          }}
          onSuggestionClick={(suggestion) => {
            console.log('Suggestion clicked:', suggestion);

            if (suggestion === 'CHANGE GENRE') {
              // Find the lyrics message to get the styles
              const lyricsMessage = messages.find(msg => msg.type === 'lyrics');
              if (lyricsMessage && lyricsMessage.styles) {
                setReferencedLyrics(lyricsMessage.styles);
                setIsStyleReference(true);
              }
            } else if (suggestion === 'EDIT LYRICS') {
              // Find the lyrics message and trigger lyrics reference
              const lyricsMessage = messages.find(msg => msg.type === 'lyrics');
              if (lyricsMessage && lyricsMessage.lyrics) {
                // Get as much lyrics as possible (remove section headers and join)
                const lyricsOnly = lyricsMessage.lyrics
                  .split('\n')
                  .filter(line => line.trim() && !line.startsWith('['))
                  .join(' ');

                // If a song is selected, reference lyrics with the song
                if (selectedSongTitle) {
                  setReferencedLyrics(`${selectedSongTitle} - ${lyricsOnly}`);
                } else {
                  setReferencedLyrics(lyricsOnly);
                }
                setIsStyleReference(false);
              }
            }
            // Handle other suggestions here
          }}
          selectedVersion={selectedVersion}
          onVersionChange={onVersionChange}
        />
      </div>
    </div>
  );
};

export default Chat;